import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/shadcn/input";
import { Search, X } from "lucide-react";

interface Props {
  searchLabel?: string;
  search: string | undefined;
  onChange: (value: string | undefined) => void;
  /** true (default): debounced for server queries. false: immediate for local filtering. */
  useDebounce?: boolean;
}

/**
 * Input for local or server-side search
 * 
 * To use local search -> useDebounce=false
 */
export function FilterSearch({ searchLabel, search, onChange, useDebounce = true }: Props) {
  const { t } = useTranslation('filters');
  const [localSearch, setLocalSearch] = useState<string | undefined>(search);

  // Sync local state if parent changes it (e.g. on reset)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // Serverseitig: debounced. Lokal: sofort durchreichen.
  useEffect(() => {
    if (!useDebounce) {
      onChange(localSearch);
      return;
    }
    const timer = setTimeout(() => {
      onChange(localSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch, useDebounce]);

  return (
    <div className="relative w-auto">
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input 
        placeholder={searchLabel ?? t('searchbar.searchPlaceholder')}
        className="pl-8 pr-8 h-9" 
        value={localSearch ?? ''}
        onChange={(e) => setLocalSearch(e.target.value)} 
      />
      {localSearch && (
        <button
          type="button"
          onClick={() => setLocalSearch(undefined)}
          className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('searchbar.searchClear')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}